import React, { useState, useEffect, useMemo } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
  selectFileIndex, selectHasAssetTags, selectHasServiceTags,
  selectHasVulnTags, selectMaxSeverity, selectMinSeverity, selectResolveHost,
  selectWorkspaceSelected
} from 'store/ManageEditCreate/selectors';
import { selectTags, selectShowTagsModal, selectTagsToAdd } from 'store/Tags/selectors';
import ModalWrapper from 'Common/Components/ModalWrapper';
import { selectLimitReached } from 'store/Host/selectors';
import TotalHostsUsed from 'Common/Components/TotalHostsUsed';
import { ACTIONS } from 'tracking/GA/constants';
import { uploadFile, setFileIndex, setUploadState, resetFileUploadState, setFilesCount, setWorkspace, setImportEntity } from 'Screens/Contextualization/ManageEditCreate/actions/Actions';
import { Button, ButtonsContainer } from 'Common/Components/MainContainer/styled';
import { Wrapper, LeftTitle, BackBtn, Content } from './styled';
import DragStep from './components/DragStep';
import ListStep from './components/ListStep';
import CreateEditTagsModal from './components/CreateEditTagsModal';
import { showProcessingQueue } from 'Common/Components/FileUploadContext/actions';
import { getTags, setTagsImportModal } from 'Screens/Contextualization/Tags/actions/Actions';
import { buildUploadParams, isAssetImport, isGeneralImport } from './utils';

const getValidFiles = (filesUploading) => Array.from(filesUploading || []).filter((file) => file && file.name);

const ImportFileCreationModal = ({ handleClose, entity, initialFiles = [] }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const initialFilesList = useMemo(() => getValidFiles(initialFiles), [initialFiles]);
  const [files, setFiles] = useState(initialFilesList);
  const [step, setStep] = useState(initialFilesList.length > 0 ? 'LIST' : 'DRAG');
  const tags = useSelector(selectTagsToAdd);
  const [tagsToAdd, setTagsToAdd] = useState(tags);
  const fileIndex = useSelector(selectFileIndex);
  const limitReached = useSelector(selectLimitReached);
  const showCreateTagModal = useSelector((state) => selectShowTagsModal('import', state));
  const resolveHost = useSelector(selectResolveHost);
  const workspace = useSelector(selectWorkspaceSelected);
  const minSeverity = useSelector(selectMinSeverity);
  const maxSeverity = useSelector(selectMaxSeverity);
  const wsTags = useSelector(selectTags);

  const titleLabel = intl.formatMessage({
    id: isAssetImport(entity) ? 'importFileCreationModal.assetsTitle' : 'importFileCreationModal.title'
  });
  const doneLabel = intl.formatMessage({ id: 'importFileCreationModal.doneButton' });
  const uploadLabel = intl.formatMessage({ id: 'importFileCreationModal.uploadButton' });
  const cancelLabel = intl.formatMessage({ id: 'importFileCreationModal.cancelButton' });
  const parsedTags = tagsToAdd.map((t) => t.name).join(',');
  const hasAssetsTags = useSelector(selectHasAssetTags);
  const hasVulnsTags = useSelector(selectHasVulnTags);
  const hasServicesTags = useSelector(selectHasServiceTags);

  useEffect(() => {
    dispatch(getTags());
    dispatch(setTagsImportModal(wsTags));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initialFilesList.length > 0) {
      dispatch(setFileIndex(initialFilesList.length - 1));
      dispatch(setUploadState());
    }
  }, [dispatch, initialFilesList]);

  useEffect(() => {
    setTagsToAdd(tags);
  }, [tags]);

  const onHandleClose = () => {
    setFiles([]);
    setStep('DRAG');
    dispatch(resetFileUploadState());
    handleClose();
    dispatch(setWorkspace(''));
  };

  const handleDrop = (filesUploading) => {
    const fileList = [...files];
    const filesUploadingList = getValidFiles(filesUploading);

    for (let i = 0; i < filesUploadingList.length; i += 1) {
      if (fileList.findIndex((item) => (item.name === filesUploadingList[i].name && item.size === filesUploadingList[i].size)) === -1) {
        fileList.push(filesUploadingList[i]);
      }
    }

    setFiles([...fileList]);
    if (fileList.length > 0) setStep('LIST');
    dispatch(setFileIndex(fileList.length - 1));
    dispatch(setUploadState());
  };

  const upload = () => {
    dispatch(setImportEntity(entity, workspace));
    const params = buildUploadParams({
      entity,
      file: files[fileIndex],
      resolveHost,
      minSeverity,
      maxSeverity,
      hasAssetsTags,
      hasVulnsTags,
      hasServicesTags,
      tags: parsedTags
    });
    setStep('UPLOAD');
    dispatch(setFilesCount(files));
    if (fileIndex >= 0) dispatch(uploadFile(params, workspace));
    dispatch(showProcessingQueue(true));
    onHandleClose();
  };

  const stepComponent = {
    LIST: <ListStep
      files={ files }
      setFiles={ setFiles }
      tags={ tagsToAdd }
      setStep={ setStep }
      handleDrop={ handleDrop }
      entity={ entity }
    />,
    DRAG: <DragStep
      files={ files }
      setFiles={ setFiles }
      tags={ tagsToAdd }
      setStep={ setStep }
      handleDrop={ handleDrop }
      entity={ entity }
    />
  };

  return (
    <ModalWrapper z={ 1000 }>
      <Wrapper>
        <LeftTitle children={ titleLabel } />
        <Content>
          { stepComponent[step] }
          { limitReached && <TotalHostsUsed trackingShow={ ACTIONS.showImportVulns } trackingClick={ ACTIONS.clickImportVulns } /> }
        </Content>
        <ButtonsContainer>
          { step === 'SUMMARY'
            ? <Button onClick={ onHandleClose } children={ doneLabel } />
            : <Button onClick={ upload } disabled={ step !== 'LIST' || isEmpty(files) || limitReached || (isGeneralImport(entity) && isEmpty(workspace)) } children={ uploadLabel } />
          }
          <BackBtn onClick={ onHandleClose } children={ cancelLabel } />
        </ButtonsContainer>
      </Wrapper>
      { showCreateTagModal && <CreateEditTagsModal setTagsToAdd={ setTagsToAdd } /> }
    </ModalWrapper>
  );
};

ImportFileCreationModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  entity: PropTypes.string,
  initialFiles: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object
  ])
};

ImportFileCreationModal.defaultProps = {
  entity: 'vulns'
};

export default ImportFileCreationModal;
